/**
 * wishlist-save-remove.js
 *
 * A save only counts once the product is filed into a named list (MA-1455 ruling):
 * after every save, open Swish's list picker; if the picker closes without a list
 * chosen, remove the item again.
 *
 * Built entirely on the Swish SDK's public surface — no DOM click capture, no polling
 * of items.list(), no manual heart/header class juggling:
 *
 *   1. `swish.intents.listen('save:item')` fires when any save completes (the
 *      <swish-save> hearts route through this intent) with the created item in
 *      `response.data`, so we know the item id immediately.
 *   2. `swish.intents.invoke('edit:item-lists', { itemId })` opens the picker. Its
 *      `complete` promise resolves `{ code: 'ok', data: { item } }` after Save (with
 *      the item's final listIds — Save with nothing ticked is still `ok` but empty) or
 *      `{ code: 'closed' }` on dismissal.
 *   3. If no list was chosen, `swish.intents.invoke('unsave:item', { itemId, delete:
 *      true })` removes the item without a confirmation. The SDK emits `item-delete`,
 *      which the hearts and the header count react to on their own.
 *
 * The save toast (`intents.saveProduct.showFeedback`) is switched off in
 * snippets/swish-assets.liquid so its own "Add to list" action doesn't overlap the picker.
 *
 * REMOVAL SPINNER (MA-2349)
 *   The heart shows a spinner while a product is being ADDED because the SDK reflects
 *   state="saving" on the <swish-save> for the duration. Removal has no such state — the
 *   element stays state="saved" until the request resolves to state="unsaved" — so the
 *   same CSS spinner never triggers on remove. We bridge that with a data-removing=""
 *   attribute (styled by the same rules as state="saving" in swish-buttons.css and
 *   blocks/wishlist-button.liquid):
 *     - STOP is the SDK-native `unsave:item` intent completion (same pattern as save).
 *     - START needs a theme-side trigger because — unlike save — the SDK exposes no
 *       "removing" state and the intent only fires on completion, not on click. A
 *       capture-phase click sets data-removing on a heart that is currently state="saved"
 *       (i.e. the click removes it); capture runs before the SDK processes the click, and
 *       document-level delegation covers JS-rendered (NG) card hearts too.
 *     - A per-heart 5s timeout clears it as a fallback (failed/suppressed removal, or the
 *       embed/intents unavailable).
 *
 * GUESTS
 *   Saving requires a signed-in customer. The Swish SDK has no "require login" option
 *   (it supports anonymous saves by design), so the theme simply does not render
 *   <swish-save> for guests: the card and PDP hearts become sign-in links instead
 *   (snippets/account-login-url.liquid with return_path: request.path, so the
 *   shopper lands back beside the product). Nothing here runs for a guest.
 *
 * SAVE BUTTON GUARD
 *   The picker's Save button stays disabled until at least one list is ticked, so a
 *   customer can't "save" a product without filing it into a list. The picker is
 *   rendered by the Swish app inside <swish-ui>'s open shadow root, so the theme can't
 *   change its markup directly; the guard watches that shadow root and keeps the submit
 *   button in step with the checkboxes. Swish places Save outside the <form>, linked by a
 *   form="..." attribute, so it is found through form.elements. Only the PICKER form
 *   (#list-select-form) is guarded: the "Create new list" / "Edit list" dialogs have their
 *   own Save button and are left alone. It watches the DOM only, so it does not depend on
 *   the SDK instance loading.
 *
 * LANGUAGE FIX
 *   Swish sends the storefront locale to the Storefront API as the `language` variable,
 *   upper-cased but otherwise as-is ("zh-TW" -> "ZH-TW"). The API's LanguageCode enum uses
 *   an underscore (ZH_TW), so on a hyphenated locale every save fails ("Variable $language
 *   of type LanguageCode! was provided invalid value") and the picker never opens. Plain
 *   locales like "en" are unaffected. The value comes from the Swish app embed and overrides
 *   anything set in window.SwishOptions, so it is corrected once the SDK instance is up:
 *   the API client reads `context.localization.language` on each request.
 */

import { getSwish } from '@theme/swish-client';

/** One picker at a time, however fast the customer works down the grid. */
let pending = false;

/**
 * @param {any} swish
 * @param {string} itemId
 */
async function fileOrRemove(swish, itemId) {
  const session = await swish.intents.invoke('edit:item-lists', { itemId });
  const outcome = await session?.complete;

  const filed = outcome?.code === 'ok' && (outcome.data?.item?.listIds?.length ?? 0) > 0;
  if (filed) return;

  // Dismissed, or saved with no list ticked. "Unsave item" inside the picker already
  // deleted it, in which case this is a tolerated no-op.
  try {
    const unsave = await swish.intents.invoke('unsave:item', { itemId, delete: true });
    await unsave?.complete;
  } catch {
    // Already gone, or the intent said no — the SDK state reflects whatever is true.
  }
}

/* ---------------------------------------------------------------------------
   Removal spinner (MA-2349) — see the REMOVAL SPINNER note above.
   --------------------------------------------------------------------------- */

const REMOVING_ATTR = 'data-removing';

/** Pending safety timers, keyed by the heart element. */
const removeTimers = new WeakMap();

/** @param {Element} el */
function startRemoving(el) {
  // Double-tap guard: a second start would stack a second timer on the same heart.
  if (el.hasAttribute(REMOVING_ATTR)) return;
  el.setAttribute(REMOVING_ATTR, '');
  removeTimers.set(el, setTimeout(() => stopRemoving(el), 5000));
}

/** @param {Element} el */
function stopRemoving(el) {
  el.removeAttribute(REMOVING_ATTR);
  const timer = removeTimers.get(el);
  if (timer) {
    clearTimeout(timer);
    removeTimers.delete(el);
  }
}

// START the spinner: read state before the SDK flips it (capture phase). The SDK has no
// "removing" state to react to (unlike save's state="saving"), so this click is the only
// signal for when a removal begins; document-level delegation covers dynamic card hearts.
document.addEventListener(
  'click',
  (event) => {
    const swishSave = event.target instanceof Element ? event.target.closest('swish-save') : null;
    if (swishSave && swishSave.getAttribute('state') === 'saved') startRemoving(swishSave);
  },
  /* useCapture = */ true,
);

/* ---------------------------------------------------------------------------
   Save button guard — see the SAVE BUTTON GUARD note above. Runs at load, not via init().
   --------------------------------------------------------------------------- */

const GUARD_STYLE_ATTR = 'data-wishlist-picker-guard';

// The picker's form. Matched by id rather than by its checkboxes, because a customer
// with no lists yet has no checkboxes at all and Save must still be disabled for them.
const PICKER_FORM_SELECTOR = /** @type {const} */ ('form#list-select-form');

// The shadow tree owns its own styling, so the disabled look has to live inside it.
const GUARD_CSS = `
  button[type="submit"][disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/** @param {ShadowRoot} root */
function addGuardStyle(root) {
  if (root.querySelector(`style[${GUARD_STYLE_ATTR}]`)) return;
  const style = document.createElement('style');
  style.setAttribute(GUARD_STYLE_ATTR, '');
  style.textContent = GUARD_CSS;
  root.appendChild(style);
}

/** @param {ShadowRoot} root */
function syncPicker(root) {
  for (const form of root.querySelectorAll(PICKER_FORM_SELECTOR)) {
    // Swish renders Save outside the <form>, tied to it by a form="..." attribute, so
    // look it up through form.elements rather than as a descendant.
    const submit = /** @type {HTMLButtonElement | undefined} */ (
      Array.from(form.elements).find((el) => el instanceof HTMLButtonElement && el.type === 'submit')
    );
    if (!submit) continue;

    const hasList = !!form.querySelector('input[type="checkbox"]:checked');
    if (submit.disabled === !hasList && submit.getAttribute('aria-disabled') === String(!hasList)) continue;
    submit.disabled = !hasList;
    submit.setAttribute('aria-disabled', String(!hasList));
  }
}

/** @param {ShadowRoot} root */
function guard(root) {
  addGuardStyle(root);
  syncPicker(root);

  // Ticks/unticks. Checkbox events are composed, so they reach the shadow root.
  root.addEventListener('change', () => syncPicker(root));
  root.addEventListener('click', () => requestAnimationFrame(() => syncPicker(root)));

  // New list rows, re-renders that reset `disabled`, the picker opening and closing.
  new MutationObserver(() => syncPicker(root)).observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'checked', 'aria-checked'],
  });
}

const guarded = new WeakSet();

function findAndGuard() {
  const ui = document.querySelector('swish-ui');
  const root = ui?.shadowRoot;
  if (!root || guarded.has(root)) return;
  guarded.add(root);
  guard(root);
}

// <swish-ui> is created by the app embed (possibly after this module runs).
findAndGuard();
new MutationObserver(findAndGuard).observe(document.documentElement, { childList: true, subtree: true });

/* ---------------------------------------------------------------------------
   Language fix — see the LANGUAGE FIX note above.
   --------------------------------------------------------------------------- */

/** @param {any} swish */
function fixLanguage(swish) {
  const localization = swish?.swishOptions?.context?.localization;
  if (typeof localization?.language === 'string' && localization.language.includes('-')) {
    localization.language = localization.language.replace(/-/g, '_');
  }
}

async function init() {
  let swish;
  try {
    swish = await getSwish();
  } catch {
    // Embed not loaded on this page: nothing to hook (the removal-spinner timeout above
    // still clears any stray attribute).
    return;
  }
  fixLanguage(swish);
  if (typeof swish.intents?.listen !== 'function') return;

  swish.intents.listen('save:item', async (response) => {
    if (response?.code !== 'ok' || pending) return;
    const itemId = response.data?.id ?? response.data?.item?.id;
    if (!itemId) return;

    pending = true;
    try {
      await fileOrRemove(swish, itemId);
    } catch {
      // Picker unavailable in this SDK build: the save stands, the customer just
      // doesn't get the list prompt.
    } finally {
      pending = false;
    }
  });

  // STOP the removal spinner on the SDK-native unsave completion. Removals happen one at a
  // time, so clearing every pending heart resolves the in-flight one.
  swish.intents.listen('unsave:item', () => {
    for (const el of document.querySelectorAll(`swish-save[${REMOVING_ATTR}]`)) stopRemoving(el);
  });
}

init();
